import { Container, Graphics, Text, type FederatedPointerEvent, type FederatedWheelEvent } from 'pixi.js';
import type { Locale, ThemePack } from '@holdline/content';
import {
  ARCHETYPES,
  CHEST_IDS,
  GEAR_SLOTS,
  HERO_IDS,
  cardCopiesToNext,
  computeMods,
  effectiveStats,
  equippedGear,
  gearMods,
  gearSets,
  heroStats,
  heroUnlocked,
  heroXpToNext,
  isEquipped,
  mergeableGear,
  nextRarity,
  type ArchetypeId,
  type Balance,
  type ChestDef,
  type EnemyArchId,
  type GearItem,
  type GearRarity,
  type GearSlot,
  type HeroId,
  type MapDef,
  type ProductId,
  type Profile,
  type SkillBranch,
  type SkillId,
  type SkinId,
  SKILL_STEP,
  streakMedals,
  skillBlocker,
  skillCost,
  skillMods,
  skillTreeTotal,
} from '@holdline/sim';
import type { BoardInfo, BoardPeriod, ChallengeView, DailyInfo, PurchaseResult, ReferralInfo, ShopInfo } from './api';
import type { QuestView } from '@holdline/sim';
import { BLEED, COLORS, FIELD_PALETTES, L, RARITY_COLORS, hexToNum } from './layout';
import { music } from './music';
import { sound } from './sound';
import { art } from './art';
import { drawGearGlyph, drawGlyph, drawHero } from './sprites';

type T = (key: string, vars?: Record<string, string | number>) => string;
export type MenuTab = 'maps' | 'cards' | 'heroes' | 'skills' | 'shop';

export interface LadderLevelView {
  n: number;
  mapId: string;
  finale: boolean;
  unlocked: boolean;
  won: boolean;
  stars: number;
  current: boolean;
}
export interface LadderView {
  total: number;
  position: number;
  chapters: { id: string; chapter: number; roster: EnemyArchId[]; finale: string | null; levels: LadderLevelView[] }[];
}

export interface MenuHooks {
  /** Open maps outside the ladder (endless) with their lock state. */
  maps: () => { id: string; def: MapDef; unlocked: boolean; current: boolean }[];
  /** The level ladder with the player's progress on it. */
  ladder: () => LadderView;
  onSelectMap: (id: string) => void;
  /** The player's progress as last confirmed by the server. */
  profile: () => Profile;
  /** Gear actions go to the server; the promise resolves once the profile above is updated. */
  gear: {
    equip: (itemId: number) => Promise<void>;
    unequip: (slot: GearSlot) => Promise<void>;
    scrap: (itemId: number) => Promise<void>;
    merge: (slot: GearSlot, rarity: GearRarity) => Promise<void>;
  };
  hero: { select: (heroId: HeroId) => Promise<void> };
  /** Skill tree purchase; resolves once the profile above is updated. */
  skills: { upgrade: (skillId: SkillId) => Promise<void> };
  /** Today's board for a map, for the Levels tab. */
  daily: (mapId: string) => Promise<DailyInfo>;
  /** Best-per-player board over a period. */
  board: (mapId: string, period: BoardPeriod) => Promise<BoardInfo>;
  /** This week's challenges with the player's completion flags. */
  challenges: () => Promise<{ week: string; list: ChallengeView[] }>;
  onPlayChallenge: (id: string) => void;
  /** The challenge being played right now, if any. */
  challengeId: string | null;
  /** Invite a friend: the link and the tally. */
  referral: () => Promise<ReferralInfo>;
  onInvite: (link: string) => void;
  /** Flip RU/EN; the page reloads in the other language. */
  onSwitchLanguage: () => void;
  /** Today's quests with progress. */
  quests: () => Promise<{ day: string; quests: QuestView[] }>;
  shop: {
    info: () => Promise<ShopInfo>;
    consent: () => Promise<void>;
    freeChest: () => Promise<PurchaseResult>;
    /** Resolves null when the player cancelled the payment. */
    buy: (product: ProductId, arch?: ArchetypeId, skin?: SkinId) => Promise<PurchaseResult | null>;
    selectSkin: (skin: SkinId) => Promise<void>;
  };
}

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const HEADER_H = 48;
const NAV_H = 56;

function label(text: string, size: number, color: number, weight: 'normal' | 'bold' = 'normal'): Text {
  return new Text({ text, style: { fill: color, fontSize: size, fontFamily: FONT, fontWeight: weight } });
}

function button(text: string, color: number, onTap: () => void, width: number, height: number, fontSize: number): Container {
  const c = new Container();
  c.addChild(new Graphics().roundRect(0, 0, width, height, 8).fill(color).stroke({ width: 1.5, color: COLORS.ink }));
  const tx = new Text({
    text,
    style: { fill: 0xffffff, fontSize, fontFamily: FONT, fontWeight: 'bold', stroke: { color: COLORS.ink, width: 2 } },
  });
  tx.anchor.set(0.5);
  tx.x = width / 2;
  tx.y = height / 2;
  c.addChild(tx);
  c.eventMode = 'static';
  c.cursor = 'pointer';
  c.on('pointertap', onTap);
  return c;
}

/**
 * Meta shell in the spirit of the reference's bottom navigation: Levels, Buildings, Heroes, Shop.
 * Levels and Buildings are live; Heroes shows the one hero we have; Shop explains what will live there.
 * The area between the header and the nav scrolls by drag or wheel when a tab is taller than the screen.
 */
export class Menu {
  readonly root = new Container();
  private tab: MenuTab = 'cards';
  private readonly header = new Container();
  private readonly content = new Container();
  private readonly nav = new Container();
  private readonly modalLayer = new Container();
  private scrollY = 0;
  private maxScroll = 0;
  private dragStart: { y: number; scroll: number } | null = null;
  private shopInfo: ShopInfo | null = null;
  private shopLoading = false;
  private quests: QuestView[] | null = null;
  private questsLoading = false;
  private challenges: ChallengeView[] | null = null;
  private challengesLoading = false;
  private referralInfo: ReferralInfo | null = null;
  private referralLoading = false;

  constructor(
    private readonly theme: ThemePack,
    private readonly locale: Locale,
    private readonly balance: Balance,
    private readonly t: T,
    private readonly hooks: MenuHooks,
    private readonly onClose: () => void,
  ) {
    this.root.visible = false;
    this.root.eventMode = 'static';
    this.root.addChild(new Graphics().rect(-BLEED, -BLEED, L.W + BLEED * 2, L.H + BLEED * 2).fill(COLORS.panel));
    const viewport = new Container();
    const mask = new Graphics().rect(0, HEADER_H, L.W, L.H - HEADER_H - NAV_H).fill(0xffffff);
    viewport.addChild(this.content);
    viewport.mask = mask;
    this.root.addChild(viewport, mask, this.header, this.nav, this.modalLayer);
    this.root.on('pointerdown', (e: FederatedPointerEvent) => {
      this.dragStart = { y: this.root.toLocal(e.global).y, scroll: this.scrollY };
    });
    this.root.on('pointermove', (e: FederatedPointerEvent) => {
      if (!this.dragStart) return;
      this.scrollTo(this.dragStart.scroll - (this.root.toLocal(e.global).y - this.dragStart.y));
    });
    this.root.on('pointerup', () => (this.dragStart = null));
    this.root.on('pointerupoutside', () => (this.dragStart = null));
    this.root.on('wheel', (e: FederatedWheelEvent) => this.scrollTo(this.scrollY + e.deltaY));
    this.render();
  }

  open(tab: MenuTab = 'cards'): void {
    this.tab = tab;
    this.scrollY = 0;
    this.root.visible = true;
    this.render();
  }

  close(): void {
    this.root.visible = false;
    this.onClose();
  }

  /** Redraw the open tab, e.g. after the profile changed. */
  refresh(): void {
    if (this.root.visible) this.render();
  }

  private scrollTo(v: number): void {
    this.scrollY = Math.max(0, Math.min(this.maxScroll, v));
    this.content.y = -this.scrollY;
  }

  private render(): void {
    this.header.removeChildren();
    this.content.removeChildren();
    this.nav.removeChildren();

    this.header.addChild(new Graphics().rect(-BLEED, -BLEED, L.W + BLEED * 2, HEADER_H + BLEED).fill(COLORS.wood));
    const title = label(this.t(`tab_${this.tab}`), 18, COLORS.text, 'bold');
    title.anchor.set(0.5);
    title.x = L.W / 2;
    title.y = HEADER_H / 2;
    this.header.addChild(title);
    // Sound toggle lives here: one tap from anywhere via the menu, no room for it in the battle HUD.
    const snd = button(sound.muted ? '🔇' : '🔊', sound.muted ? 0x3a3f4b : COLORS.woodLight, () => {
      sound.toggle();
      sound.play('tap');
      this.render();
    }, 40, 30, 13);
    snd.x = L.W - 40 - 10;
    snd.y = (HEADER_H - 30) / 2;
    this.header.addChild(snd);
    const mus = button(music.muted ? '🎵̸' : '🎵', music.muted ? 0x3a3f4b : COLORS.woodLight, () => {
      music.toggle();
      sound.play('tap');
      this.render();
    }, 40, 30, 13);
    mus.x = L.W - 40 - 10 - 46;
    mus.y = (HEADER_H - 30) / 2;
    this.header.addChild(mus);
    const lang = button(this.locale === 'ru' ? 'EN' : 'RU', 0x3a3f4b, () => {
      sound.play('tap');
      this.hooks.onSwitchLanguage();
    }, 40, 30, 12);
    lang.x = 10;
    lang.y = (HEADER_H - 30) / 2;
    this.header.addChild(lang);

    if (this.tab === 'cards') this.renderCards();
    else if (this.tab === 'maps') this.renderMaps();
    else if (this.tab === 'heroes') this.renderHeroes();
    else if (this.tab === 'skills') this.renderSkills();
    else this.renderShop();

    // How far the tab may scroll: its lowest pixel against the top edge of the nav.
    const bottom = this.content.getLocalBounds().maxY + 12;
    this.maxScroll = Math.max(0, bottom - (L.H - NAV_H));
    this.scrollTo(this.scrollY);

    const navY = L.H - NAV_H;
    this.nav.addChild(new Graphics().rect(-BLEED, navY, L.W + BLEED * 2, NAV_H + BLEED).fill(COLORS.wood));
    const tabs: { id: MenuTab | 'battle'; key: string }[] = [
      { id: 'battle', key: 'tab_battle' },
      { id: 'maps', key: 'tab_maps' },
      { id: 'cards', key: 'tab_cards' },
      { id: 'heroes', key: 'tab_heroes' },
      { id: 'skills', key: 'tab_skills' },
      { id: 'shop', key: 'tab_shop' },
    ];
    const w = L.W / tabs.length;
    tabs.forEach((tb, i) => {
      const c = new Container();
      c.x = i * w;
      c.y = navY;
      const active = tb.id === this.tab;
      c.addChild(new Graphics().roundRect(4, 6, w - 8, 44, 10).fill(active ? COLORS.woodLight : COLORS.wood));
      const tx = label(this.t(tb.key), 10.5, active ? COLORS.text : COLORS.textDim, 'bold');
      tx.anchor.set(0.5);
      tx.x = w / 2;
      tx.y = 28;
      c.addChild(tx);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => {
        if (tb.id === 'battle') this.close();
        else {
          this.tab = tb.id;
          this.scrollY = 0;
          this.render();
        }
      });
      this.nav.addChild(c);
    });
  }

  private card(y: number, h: number): Container {
    const row = new Container();
    row.y = y;
    row.addChild(new Graphics().roundRect(10, 0, L.W - 20, h, 10).fill(0x232833).stroke({ width: 1, color: COLORS.panelLine }));
    return row;
  }

  private hint(key: string): void {
    const hint = label(this.t(key), 11, COLORS.textDim);
    hint.style.wordWrap = true;
    hint.style.wordWrapWidth = L.W - 24;
    hint.x = 12;
    hint.y = HEADER_H + 8;
    this.content.addChild(hint);
  }

  /** "копия / копии / копий" by Russian rules; English only needs one vs many. */
  private copiesWord(n: number): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    const form = this.locale === 'ru' ? (mod10 === 1 && mod100 !== 11 ? 'one' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'few' : 'many') : n === 1 ? 'one' : 'many';
    return this.t(`copies_word_${form}`);
  }

  /** Human text for a quest, in the current language. */
  questText(q: QuestView): string {
    if (q.kind === 'win_map') return this.t('quest_win_map', { name: this.t(`${q.mapId ?? ''}_name`) });
    return this.t(`quest_${q.kind}`, { n: q.target });
  }

  private loadReferral(): void {
    if (this.referralLoading) return;
    this.referralLoading = true;
    this.hooks
      .referral()
      .then((r) => {
        this.referralInfo = r;
      })
      .catch(() => {
        this.referralInfo = null;
      })
      .finally(() => {
        this.referralLoading = false;
        if (this.root.visible && this.tab === 'maps') this.render();
      });
  }

  private loadChallenges(): void {
    if (this.challengesLoading) return;
    this.challengesLoading = true;
    this.hooks
      .challenges()
      .then((r) => {
        this.challenges = r.list;
      })
      .catch(() => {
        this.challenges = null;
      })
      .finally(() => {
        this.challengesLoading = false;
        if (this.root.visible && this.tab === 'maps') this.render();
      });
  }

  private loadQuests(): void {
    if (this.questsLoading) return;
    this.questsLoading = true;
    this.hooks
      .quests()
      .then((r) => {
        this.quests = r.quests;
      })
      .catch(() => {
        this.quests = null;
      })
      .finally(() => {
        this.questsLoading = false;
        if (this.tab === 'maps' && this.root.visible) this.render();
      });
  }

  /** Refresh the quest list after a run or a chest changed it. */
  setQuests(list: QuestView[]): void {
    this.quests = list;
    this.refresh();
  }

  private renderMaps(): void {
    this.hint('zone_grows');
    let y = 90;
    // The ladder: chapters of ten nodes, the player's position lit, stars under cleared levels.
    {
      const lad = this.hooks.ladder();
      const head = label(this.t('ladder_progress', { n: lad.position, total: lad.total }), 14, COLORS.text, 'bold');
      head.x = 16;
      head.y = y;
      this.content.addChild(head);
      const hint = label(this.t('ladder_hint'), 9.5, COLORS.textDim);
      hint.x = 16;
      hint.y = y + 20;
      hint.style.wordWrap = true;
      hint.style.wordWrapWidth = L.W - 32;
      this.content.addChild(hint);
      y += 24 + Math.ceil(hint.height) + 6;
      let seen = new Set<EnemyArchId>();
      for (const ch of lad.chapters) {
        const fresh = ch.roster.filter((a) => !seen.has(a));
        seen = new Set([...seen, ...ch.roster]);
        const anyOpen = ch.levels.some((l) => l.unlocked);
        const row = this.card(y, 104);
        const title = label(this.t('chapter_title', { n: ch.chapter, name: this.t(`chapter_${ch.id}`) }), 13, anyOpen ? COLORS.text : COLORS.textDim, 'bold');
        title.x = 22;
        title.y = 8;
        row.addChild(title);
        if (fresh.length > 0) {
          const nw = label(this.t('chapter_new', { list: fresh.map((a) => this.theme.enemies[a].name[this.locale]).join(', ') }), 9, anyOpen ? 0xff9f43 : COLORS.textDim);
          nw.anchor.set(1, 0);
          nw.x = L.W - 22;
          nw.y = 11;
          row.addChild(nw);
        }
        // Ten nodes in one line: 28 px circles, the finale a square.
        const nodeW = 28;
        const gapX = Math.floor((L.W - 44 - nodeW * ch.levels.length) / (ch.levels.length - 1));
        ch.levels.forEach((lv, i) => {
          const c = new Container();
          c.x = 22 + i * (nodeW + gapX);
          c.y = 32;
          const fill = lv.current ? 0x3a7d44 : lv.won ? 0x2b6cb0 : lv.unlocked ? 0xb8651b : 0x2b3140;
          const g = new Graphics();
          if (lv.finale) g.roundRect(0, 0, nodeW, nodeW, 6).fill(fill).stroke({ width: lv.current ? 2 : 1, color: lv.current ? COLORS.gold : COLORS.panelLine });
          else g.circle(nodeW / 2, nodeW / 2, nodeW / 2).fill(fill).stroke({ width: lv.current ? 2 : 1, color: lv.current ? COLORS.gold : COLORS.panelLine });
          c.addChild(g);
          const num = label(lv.unlocked ? String(lv.n) : '🔒', lv.unlocked ? 10 : 9, lv.unlocked ? COLORS.text : COLORS.textDim, 'bold');
          num.anchor.set(0.5);
          num.x = nodeW / 2;
          num.y = nodeW / 2;
          c.addChild(num);
          const st = label(lv.won ? '★★★'.slice(0, lv.stars) + '☆☆☆'.slice(0, 3 - lv.stars) : '', 8, COLORS.gold);
          st.anchor.set(0.5, 0);
          st.x = nodeW / 2;
          st.y = nodeW + 2;
          c.addChild(st);
          if (lv.unlocked && !lv.current) {
            c.eventMode = 'static';
            c.cursor = 'pointer';
            c.on('pointertap', () => this.hooks.onSelectMap(lv.mapId));
          } else if (!lv.unlocked) {
            c.eventMode = 'static';
            c.on('pointertap', () => this.showError('level_locked'));
          }
          row.addChild(c);
        });
        // The finale's board is where the chapter competes.
        if (ch.finale && ch.levels[ch.levels.length - 1]!.unlocked) {
          const top = button(this.t('top_today'), 0x3a3f4b, () => this.showBoard(ch.finale!), 84, 24, 10);
          top.x = L.W - 22 - 84;
          top.y = 74;
          row.addChild(top);
        }
        const foot = label(this.t(`${ch.finale ?? 'l60'}_name`) === `${ch.finale ?? 'l60'}_name` ? '' : this.t(`${ch.finale}_name`), 9, COLORS.textDim);
        foot.x = 22;
        foot.y = 80;
        row.addChild(foot);
        this.content.addChild(row);
        y += 110;
      }
    }
    // Daily quests: the reason to come back today.
    const head = label(this.t('quests_title'), 14, COLORS.text, 'bold');
    head.x = 16;
    head.y = y;
    this.content.addChild(head);
    const reset = label(this.t('quests_reset'), 9, COLORS.textDim);
    reset.anchor.set(1, 0);
    reset.x = L.W - 16;
    reset.y = y + 4;
    this.content.addChild(reset);
    y += 24;
    // Login streak: consecutive days played and what tomorrow's first run pays.
    {
      const st = this.hooks.profile().streak;
      const next = streakMedals(st.count + 1, this.balance);
      const line = label(`🔥 ${this.t('streak_title', { n: st.count })}  ·  ${this.t('streak_next', { n: next })}`, 11, st.count > 0 ? 0xff9f43 : COLORS.textDim, 'bold');
      line.x = 16;
      line.y = y;
      this.content.addChild(line);
      y += 22;
    }
    if (!this.quests) {
      const tx = label(this.questsLoading ? this.t('loading') : this.t('server_down'), 11, COLORS.textDim);
      tx.x = 16;
      tx.y = y;
      this.content.addChild(tx);
      if (!this.questsLoading) this.loadQuests();
      y += 26;
    } else {
      for (const q of this.quests) {
        const row = this.card(y, 44);
        const text = label(this.questText(q), 11, q.done ? COLORS.textDim : COLORS.text, 'bold');
        text.x = 22;
        text.y = 6;
        text.style.wordWrap = true;
        text.style.wordWrapWidth = L.W - 150;
        const reward = label(this.t('quest_reward', { copies: q.copies, word: this.copiesWord(q.copies), xp: q.heroXp }), 9, COLORS.gold);
        reward.x = 22;
        reward.y = 25;
        row.addChild(text, reward);
        // Progress: a small bar with the numbers, or a check when done.
        const bw = 90;
        const bx = L.W - 22 - bw;
        const bar = new Graphics();
        bar.roundRect(bx, 16, bw, 8, 4).fill(COLORS.hpBg);
        bar.roundRect(bx, 16, Math.max(0, Math.min(bw, (bw * q.progress) / Math.max(1, q.target))), 8, 4).fill(q.done ? COLORS.hp : COLORS.accent);
        row.addChild(bar);
        const num = label(q.done ? `✓ ${this.t('quest_done')}` : `${q.progress}/${q.target}`, 10, q.done ? COLORS.hp : COLORS.textDim, 'bold');
        num.anchor.set(0.5, 0);
        num.x = bx + bw / 2;
        num.y = 26;
        row.addChild(num);
        this.content.addChild(row);
        y += 50;
      }
    }
    // Invite a friend: both sides get medals once the friend clears the goal.
    {
      const ref = this.referralInfo;
      if (!ref && !this.referralLoading) this.loadReferral();
      const row = this.card(y, 58);
      const title = label(this.t('invite_title'), 13, COLORS.text, 'bold');
      title.x = 22;
      title.y = 8;
      const goal = ref ? ref.goalWaves : this.balance.referral.goalWaves;
      const each = ref ? ref.medalsEach : this.balance.referral.medalsEach;
      const sub = label(`${this.t('invite_rule', { n: goal, m: each })}${ref ? ` · ${this.t('invite_count', { n: ref.invites })}` : ''}`, 9.5, COLORS.textDim);
      sub.x = 22;
      sub.y = 30;
      sub.style.wordWrap = true;
      sub.style.wordWrapWidth = L.W - 170;
      row.addChild(title, sub);
      const link = ref?.link ?? null;
      const btn = button(this.t(link ? 'invite_button' : 'invite_offline'), link ? 0x2a9d8f : COLORS.buttonDisabled, () => {
        if (link) this.hooks.onInvite(link);
      }, 118, 34, 11.5);
      btn.x = L.W - 22 - 118;
      btn.y = 12;
      row.addChild(btn);
      this.content.addChild(row);
      y += 64;
    }
    // Weekly challenges: a map under modifiers for medals, once a week each.
    {
      const head2 = label(this.t('challenges_title'), 14, COLORS.text, 'bold');
      head2.x = 16;
      head2.y = y + 8;
      this.content.addChild(head2);
      const reset2 = label(this.t('challenges_reset'), 9, COLORS.textDim);
      reset2.anchor.set(1, 0);
      reset2.x = L.W - 16;
      reset2.y = y + 12;
      this.content.addChild(reset2);
      y += 32;
      if (!this.challenges) {
        const tx = label(this.challengesLoading ? this.t('loading') : this.t('server_down'), 11, COLORS.textDim);
        tx.x = 16;
        tx.y = y;
        this.content.addChild(tx);
        if (!this.challengesLoading) this.loadChallenges();
        y += 26;
      } else {
        // A challenge on a chapter finale opens with that level on the ladder; open maps keep their own rule.
        const unlocked = new Set(this.hooks.maps().filter((m) => m.unlocked).map((m) => m.id));
        for (const ch of this.hooks.ladder().chapters) for (const lv of ch.levels) if (lv.unlocked) unlocked.add(lv.mapId);
        for (const c of this.challenges) {
          const row = this.card(y, 74);
          const nm = label(this.theme.challenges[c.id]?.name[this.locale] ?? c.id, 13, c.done ? COLORS.textDim : COLORS.text, 'bold');
          nm.x = 22;
          nm.y = 8;
          const mods = label(`${this.t(`${c.mapId}_name`)} · ${c.modifiers.map((m) => this.theme.modifiers[m].name[this.locale]).join(' · ')}`, 10, 0xff9f43);
          mods.x = 22;
          mods.y = 28;
          mods.style.wordWrap = true;
          mods.style.wordWrapWidth = L.W - 160;
          const reward = label(this.t('challenge_reward', { n: c.medals, w: c.goalWaves }), 10, COLORS.gold, 'bold');
          reward.x = 22;
          reward.y = 54;
          row.addChild(nm, mods, reward);
          const bw = 104;
          if (c.done) {
            const done = label(`✓ ${this.t('challenge_done')}`, 12, COLORS.hp, 'bold');
            done.anchor.set(1, 0.5);
            done.x = L.W - 22;
            done.y = 37;
            row.addChild(done);
          } else if (!unlocked.has(c.mapId)) {
            const lock = label(this.t('challenge_locked', { name: this.t(`${c.mapId}_name`) }), 9.5, COLORS.textDim);
            lock.anchor.set(1, 0.5);
            lock.x = L.W - 22;
            lock.y = 37;
            lock.style.wordWrap = true;
            lock.style.wordWrapWidth = 110;
            lock.style.align = 'right';
            row.addChild(lock);
          } else {
            const current = this.hooks.challengeId === c.id;
            const btn = button(current ? this.t('selected') : this.t('challenge_play'), current ? 0x3a7d44 : 0xb8651b, () => {
              if (!current) this.hooks.onPlayChallenge(c.id);
            }, bw, 34, 12);
            btn.x = L.W - 22 - bw;
            btn.y = 20;
            row.addChild(btn);
          }
          this.content.addChild(row);
          y += 80;
        }
      }
      y += 6;
    }
    y += 10;
    {
      const openHead = label(this.t('open_maps'), 14, COLORS.text, 'bold');
      openHead.x = 16;
      openHead.y = y + 4;
      this.content.addChild(openHead);
      y += 28;
    }
    this.hooks.maps().forEach((m, i) => {
      const row = this.card(y + i * 96, 86);
      const name = label(`${i + 1}. ${this.t(`${m.id}_name`)}`, 16, m.unlocked ? COLORS.text : COLORS.textDim, 'bold');
      name.x = 22;
      name.y = 12;
      const info = label(
        `${m.def.endless ? this.t('waves_endless') : this.t('waves_count', { n: m.def.waves.length })} · ${m.def.width}×${m.def.height}`,
        11,
        m.def.endless ? RARITY_COLORS.epic : COLORS.textDim,
      );
      info.x = 22;
      info.y = 38;
      row.addChild(name, info);
      // Tiny preview of the obstacle layout
      const pv = new Graphics();
      const cs = 6;
      const px = L.W - 24 - m.def.width * cs;
      for (let y = 0; y < m.def.height; y++) {
        for (let x = 0; x < m.def.width; x++) pv.rect(px + x * cs, 10 + y * cs, cs - 1, cs - 1).fill(COLORS.grassB);
      }
      for (const c of m.def.closed) pv.rect(px + c.x * cs, 10 + c.y * cs, cs - 1, cs - 1).fill(c.cost > 0 ? COLORS.rock : COLORS.rockDark);
      if (m.def.base) pv.rect(px + m.def.base.x * cs, 10 + m.def.base.y * cs, m.def.base.w * cs - 1, m.def.base.h * cs - 1).fill(0xdfe6e9);
      row.addChild(pv);
      const btnText = m.current ? this.t('selected') : m.unlocked ? this.t('select') : this.t('locked');
      const btn = new Container();
      btn.addChild(new Graphics().roundRect(0, 0, 96, 30, 8).fill(m.current ? 0x3a7d44 : m.unlocked ? COLORS.button : COLORS.buttonDisabled));
      const bt = label(btnText, 12, COLORS.text, 'bold');
      bt.anchor.set(0.5);
      bt.x = 48;
      bt.y = 15;
      btn.addChild(bt);
      btn.x = 22;
      btn.y = 52;
      if (m.unlocked && !m.current) {
        btn.eventMode = 'static';
        btn.cursor = 'pointer';
        btn.on('pointertap', () => this.hooks.onSelectMap(m.id));
      }
      row.addChild(btn);
      if (m.unlocked) {
        const top = button(this.t('top_today'), 0x3a3f4b, () => this.showBoard(m.id), 84, 30, 11);
        top.x = 126;
        top.y = 52;
        row.addChild(top);
      }
      if (!m.unlocked) {
        const req = m.def.requires;
        const lock = label(req ? this.t('unlock_hint_named', { name: this.t(`${req}_name`) }) : this.t('unlock_hint'), 10, COLORS.textDim);
        lock.x = 126;
        lock.y = 60;
        lock.style.wordWrap = true;
        lock.style.wordWrapWidth = L.W - 160;
        row.addChild(lock);
      }
      this.content.addChild(row);
    });
  }

  private renderCards(): void {
    this.hint('cards_hint');
    const cap = label(this.t('cap_hint', { n: this.balance.soldierCap }), 10.5, COLORS.accent);
    cap.style.wordWrap = true;
    cap.style.wordWrapWidth = L.W - 24;
    cap.x = 12;
    cap.y = HEADER_H + 50;
    this.content.addChild(cap);
    const mods = computeMods([]);
    const profile = this.hooks.profile();
    const skills = skillMods(profile.skills);
    // Two lines of description fit; the list scrolls past the nav.
    const rowH = 70;
    const startY = 132;
    ARCHETYPES.forEach((arch: ArchetypeId, i) => {
      const th = this.theme.units[arch];
      const card = profile.cards[arch] ?? { level: 1, copies: 0 };
      const st = effectiveStats({ id: 0, arch, level: 1 }, this.balance, mods, card.level, skills);
      const row = this.card(startY + i * rowH, rowH - 6);
      const pic = art.sprite(`unit:${arch}`, 46, 46);
      if (pic) {
        pic.x = 36;
        pic.y = 32;
        row.addChild(pic);
      } else {
        const icon = new Graphics().roundRect(18, 14, 36, 36, 8).fill(hexToNum(th.color)).stroke({ width: 2, color: COLORS.ink });
        drawGlyph(icon, arch, 36, 32, 7);
        row.addChild(icon);
      }
      const name = label(th.name[this.locale], 13, COLORS.text, 'bold');
      name.x = 64;
      name.y = 7;
      row.addChild(name);
      const need = cardCopiesToNext(card.level, this.balance);
      const progress = need === null ? this.t('card_max') : this.t('copies_to_next', { n: card.copies, total: need, lvl: card.level + 1 });
      const lvl = label(`${this.t('card_level', { n: card.level })} · ${progress}`, 10, card.level > 1 ? COLORS.gold : COLORS.textDim);
      lvl.anchor.set(1, 0);
      lvl.x = L.W - 20;
      lvl.y = 9;
      row.addChild(lvl);
      const parts: string[] = [this.t('hp', { n: st.hp })];
      if (st.cooldown > 0) parts.push(this.t('dmg', { n: st.dmg }), this.t('range', { n: (st.range / 1000).toFixed(1) }));
      if (st.soldier) parts.push(this.t('soldier', { name: this.theme.soldiers[arch]?.name[this.locale] ?? '' }));
      if (st.heal > 0 && st.healInterval > 0) parts.push(this.t('heal', { n: ((st.heal * 20) / st.healInterval).toFixed(1) }));
      if (st.income > 0) parts.push(this.t('income', { n: st.income }));
      const stats = label(parts.join(' · '), 10, COLORS.accent);
      stats.x = 64;
      stats.y = 25;
      row.addChild(stats);
      const desc = label(th.desc[this.locale], 10, COLORS.textDim);
      desc.x = 64;
      desc.y = 39;
      desc.style.wordWrap = true;
      desc.style.wordWrapWidth = L.W - 90;
      row.addChild(desc);
      this.content.addChild(row);
    });
  }

  private renderHeroes(): void {
    const profile = this.hooks.profile();
    const hero = profile.hero;
    const heroId = hero.selected;
    const th = this.theme.heroes[heroId];
    const worn = equippedGear(profile);
    const st = heroStats(this.balance, heroId, hero.level, gearMods(worn, this.balance), skillMods(profile.skills));
    const sets = gearSets(worn, this.balance);
    const gearName = (it: GearItem): string => `${this.t(`rarity_${it.rarity}`)} ${this.theme.gear[it.slot].name[this.locale]}`;
    const act = (p: Promise<void>): void => {
      p.then(() => this.render()).catch(() => this.render());
    };

    // Who walks out of the bunker: one tile per hero, locked ones say when they open.
    const pickW = Math.floor((L.W - 32 - (HERO_IDS.length - 1) * 8) / HERO_IDS.length);
    HERO_IDS.forEach((id, i) => {
      const def = this.balance.heroes[id];
      const t = this.theme.heroes[id];
      const unlocked = heroUnlocked(profile, id, this.balance);
      const selected = id === heroId;
      const c = new Container();
      c.x = 16 + i * (pickW + 8);
      c.y = 58;
      c.addChild(new Graphics().roundRect(0, 0, pickW, 48, 10).fill(selected ? 0x2b3140 : 0x232833).stroke({ width: selected ? 2 : 1, color: selected ? hexToNum(t.color) : COLORS.panelLine }));
      const facePic = art.sprite(`hero:${id}`, 36, 40);
      if (facePic) {
        facePic.x = 22;
        facePic.y = 24;
        facePic.alpha = unlocked ? 1 : 0.35;
        c.addChild(facePic);
      } else {
        const face = new Graphics();
        drawHero(face, 22, 24, unlocked ? hexToNum(t.color) : 0x4a5160, id);
        c.addChild(face);
      }
      const nm = label(t.name[this.locale], 10.5, unlocked ? COLORS.text : COLORS.textDim, 'bold');
      nm.x = 42;
      nm.y = 9;
      const sub = label(
        selected ? this.t('hero_selected') : unlocked ? this.t('hero_select') : this.t('hero_locked_at', { n: def.unlockLevel }),
        9.5,
        selected ? COLORS.hp : unlocked ? COLORS.accent : COLORS.textDim,
      );
      sub.x = 42;
      sub.y = 26;
      c.addChild(nm, sub);
      if (unlocked && !selected) {
        c.eventMode = 'static';
        c.cursor = 'pointer';
        c.on('pointertap', () => act(this.hooks.hero.select(id)));
      }
      this.content.addChild(c);
    });

    // Hero card
    const row = this.card(114, 130);
    const portrait = new Graphics();
    portrait.roundRect(16, 14, 64, 64, 12).fill(0x1b1e26).stroke({ width: 2, color: hexToNum(th.color) });
    const portraitPic = art.sprite(`hero:${heroId}`, 56, 58);
    if (!portraitPic) drawHero(portrait, 48, 48, hexToNum(th.color), heroId);
    row.addChild(portrait);
    if (portraitPic) {
      portraitPic.x = 48;
      portraitPic.y = 47;
      row.addChild(portraitPic);
    }
    const name = label(th.name[this.locale], 18, hexToNum(th.color), 'bold');
    name.x = 94;
    name.y = 10;
    const need = heroXpToNext(hero.level, this.balance);
    const xpText = need === null ? this.t('card_max') : this.t('hero_xp', { xp: hero.xp, need, n: hero.level + 1 });
    const lvl = label(`${this.t('hero_level', { n: hero.level })} · ${xpText}`, 11, COLORS.textDim);
    lvl.x = 94;
    lvl.y = 34;
    const sk = st.skill;
    const skillText =
      sk.kind === 'shockwave'
        ? this.t('hero_shockwave', { r: (sk.radius / 1000).toFixed(1), n: sk.every })
        : sk.kind === 'mend'
          ? this.t('hero_mend', { n: sk.every, w: sk.wallHeal, s: sk.structureHeal })
          : this.t('hero_snipe', { n: sk.every, k: (sk.pct / 100).toFixed(1) });
    const stats = label(
      [this.t('hp', { n: st.hp }), this.t('dmg', { n: st.dmg }), this.t('dps', { n: Math.round((st.dmg * 20) / st.interval) }), this.t('hero_speed', { n: ((st.speed * 20) / 1000).toFixed(1) }), skillText].join(' · '),
      10,
      COLORS.accent,
    );
    stats.x = 94;
    stats.y = 52;
    stats.style.wordWrap = true;
    stats.style.wordWrapWidth = L.W - 120;
    const skill = label(th.skill[this.locale], 11, COLORS.text, 'bold');
    skill.x = 16;
    skill.y = 92;
    skill.style.wordWrap = true;
    skill.style.wordWrapWidth = L.W - 52;
    row.addChild(name, lvl, stats, skill);
    this.content.addChild(row);

    // Four slots: tap a worn item to take it off.
    const tileW = 78;
    const tileH = 74;
    const gap = 6;
    const x0 = Math.floor((L.W - (tileW * 4 + gap * 3)) / 2);
    const slotsY = 254;
    GEAR_SLOTS.forEach((slot, i) => {
      const item = worn.find((it) => it.slot === slot);
      const color = item ? RARITY_COLORS[item.rarity] : COLORS.panelLine;
      const c = new Container();
      c.x = x0 + i * (tileW + gap);
      c.y = slotsY;
      c.addChild(new Graphics().roundRect(0, 0, tileW, tileH, 10).fill(0x232833).stroke({ width: item ? 2 : 1, color }));
      const slotPic = art.sprite(`gear:${slot}`, 40, 40);
      if (slotPic) {
        slotPic.x = tileW / 2;
        slotPic.y = 24;
        slotPic.alpha = item ? 1 : 0.4;
        c.addChild(slotPic);
      } else {
        const icon = new Graphics();
        drawGearGlyph(icon, slot, tileW / 2, 24, 9, item ? color : 0x3a4150);
        c.addChild(icon);
      }
      const nm = label(this.theme.gear[slot].name[this.locale], 8.5, COLORS.textDim);
      nm.anchor.set(0.5, 0);
      nm.x = tileW / 2;
      nm.y = 44;
      c.addChild(nm);
      const val = label(item ? `+${item.bonus}%` : this.t('empty_slot'), 10, item ? COLORS.gold : 0x4a5160, 'bold');
      val.anchor.set(0.5, 0);
      val.x = tileW / 2;
      val.y = 57;
      c.addChild(val);
      if (item) {
        c.eventMode = 'static';
        c.cursor = 'pointer';
        c.on('pointertap', () => act(this.hooks.gear.unequip(slot)));
      }
      this.content.addChild(c);
    });

    const hint = label(this.t('gear_hint'), 10, COLORS.textDim);
    hint.style.wordWrap = true;
    hint.style.wordWrapWidth = L.W - 32;
    hint.x = 16;
    hint.y = slotsY + tileH + 8;
    this.content.addChild(hint);

    // Set bonuses: what the loadout earns, lit when active.
    const setLines: { text: string; on: boolean }[] = [
      { text: this.t('set_pair', { n: this.balance.gear.sets.pairPct }), on: sets.pair },
      { text: this.t('set_epic'), on: sets.fullEpic },
    ];
    let setY = hint.y + Math.ceil(hint.height) + 6;
    for (const line of setLines) {
      const tx = label(`${line.on ? this.t('set_active') + ' ' : ''}${line.text}`, 10, line.on ? COLORS.hp : 0x5a6270, line.on ? 'bold' : 'normal');
      tx.style.wordWrap = true;
      tx.style.wordWrapWidth = L.W - 32;
      tx.x = 16;
      tx.y = setY;
      this.content.addChild(tx);
      setY += Math.ceil(tx.height) + 3;
    }

    // Bag
    const cap = this.balance.gear.inventoryCap;
    const headY = setY + 10;
    const head = label(this.t('inventory', { n: hero.inventory.length, cap }), 13, COLORS.text, 'bold');
    head.x = 16;
    head.y = headY;
    this.content.addChild(head);
    if (hero.inventory.length === 0) {
      const empty = label(this.t('inventory_empty', { n: this.balance.gear.dropMinWaves }), 11, COLORS.textDim);
      empty.style.wordWrap = true;
      empty.style.wordWrapWidth = L.W - 32;
      empty.x = 16;
      empty.y = headY + 24;
      this.content.addChild(empty);
      return;
    }
    const rarityRank = (r: GearRarity): number => (r === 'epic' ? 0 : r === 'rare' ? 1 : 2);
    const items = [...hero.inventory].sort((a, b) => {
      const ea = isEquipped(profile, a.id) ? 0 : 1;
      const eb = isEquipped(profile, b.id) ? 0 : 1;
      return ea - eb || GEAR_SLOTS.indexOf(a.slot) - GEAR_SLOTS.indexOf(b.slot) || rarityRank(a.rarity) - rarityRank(b.rarity) || b.bonus - a.bonus || a.id - b.id;
    });
    const rowH = 46;
    items.forEach((it, i) => {
      const r = this.card(headY + 24 + i * rowH, rowH - 6);
      const color = RARITY_COLORS[it.rarity];
      const wornNow = isEquipped(profile, it.id);
      const itemPic = art.sprite(`gear:${it.slot}`, 30, 30);
      if (itemPic) {
        itemPic.x = 30;
        itemPic.y = 20;
        r.addChild(itemPic);
      } else {
        const icon = new Graphics();
        drawGearGlyph(icon, it.slot, 30, 20, 6, color);
        r.addChild(icon);
      }
      const nm = label(gearName(it), 10.5, color, 'bold');
      nm.x = 46;
      nm.y = 5;
      const stat = label(this.t(`gear_stat_${it.slot}`, { n: it.bonus }), 9, COLORS.textDim);
      stat.x = 46;
      stat.y = 22;
      r.addChild(nm, stat);
      if (wornNow) {
        const tag = label(this.t('equipped'), 10, COLORS.hp, 'bold');
        tag.anchor.set(1, 0.5);
        tag.x = L.W - 92;
        tag.y = 20;
        r.addChild(tag);
        const off = button(this.t('unequip'), 0x3a3f4b, () => act(this.hooks.gear.unequip(it.slot)), 60, 28, 10.5);
        off.x = L.W - 84;
        off.y = 6;
        r.addChild(off);
        this.content.addChild(r);
        return;
      }
      const on = button(this.t('equip'), COLORS.button, () => act(this.hooks.gear.equip(it.id)), 54, 28, 10.5);
      on.x = L.W - 162;
      on.y = 6;
      r.addChild(on);
      const next = nextRarity(it.rarity);
      const pool = mergeableGear(profile, it.slot, it.rarity);
      if (next && pool.length >= this.balance.gear.mergeCount) {
        const mergeBtn = button(
          this.t('merge_three', { n: this.balance.gear.mergeCount, rarity: this.t(`rarity_${next}`).toLocaleLowerCase(this.locale) }),
          0x5b3fa8,
          () => act(this.hooks.gear.merge(it.slot, it.rarity)),
          92,
          28,
          9,
        );
        mergeBtn.x = L.W - 104;
        mergeBtn.y = 6;
        r.addChild(mergeBtn);
      } else {
        const scrap = button(this.t('scrap_for', { n: this.balance.gear.scrapXp[it.rarity] }), 0x6b3f3f, () => act(this.hooks.gear.scrap(it.id)), 92, 28, 9);
        scrap.x = L.W - 104;
        scrap.y = 6;
        r.addChild(scrap);
      }
      this.content.addChild(r);
    });
  }

  // ---------- modal ----------

  private showModal(title: string, lines: { text: string; color: number }[], buttons: { label: string; color: number; onTap: () => void; fontSize?: number }[]): void {
    this.modalLayer.removeChildren();
    const dim = new Graphics().rect(-BLEED, -BLEED, L.W + BLEED * 2, L.H + BLEED * 2).fill({ color: 0x000000, alpha: 0.7 });
    dim.eventMode = 'static';
    this.modalLayer.addChild(dim);
    const w = L.W - 40;
    const card = new Container();
    card.x = 20;
    const head = label(title, 17, COLORS.text, 'bold');
    head.style.wordWrap = true;
    head.style.wordWrapWidth = w - 32;
    head.style.align = 'center';
    head.anchor.set(0.5, 0);
    head.x = w / 2;
    head.y = 16;
    let y = 16 + Math.ceil(head.height) + 12;
    const body: Text[] = [];
    for (const line of lines) {
      const tx = label(line.text, 12, line.color);
      tx.style.wordWrap = true;
      tx.style.wordWrapWidth = w - 32;
      tx.style.align = 'center';
      tx.anchor.set(0.5, 0);
      tx.x = w / 2;
      tx.y = y;
      y += Math.ceil(tx.height) + 6;
      body.push(tx);
    }
    y += 10;
    const bw = Math.min(140, (w - 32 - (buttons.length - 1) * 10) / Math.max(1, buttons.length));
    const total = buttons.length * bw + (buttons.length - 1) * 10;
    buttons.forEach((b, i) => {
      const btn = button(b.label, b.color, b.onTap, bw, 40, b.fontSize ?? 13);
      btn.x = (w - total) / 2 + i * (bw + 10);
      btn.y = y;
      card.addChild(btn);
    });
    const h = y + (buttons.length > 0 ? 40 : 0) + 16;
    card.addChildAt(new Graphics().roundRect(0, 0, w, h, 14).fill(COLORS.panel).stroke({ width: 2, color: COLORS.accent }), 0);
    card.addChild(head, ...body);
    card.y = Math.max(40, Math.floor((L.H - h) / 2));
    this.modalLayer.addChild(card);
  }

  private closeModal(): void {
    this.modalLayer.removeChildren();
  }

  // ---------- shop ----------

  private loadShop(): void {
    if (this.shopLoading) return;
    this.shopLoading = true;
    this.hooks.shop
      .info()
      .then((info) => {
        this.shopInfo = info;
      })
      .catch(() => {
        this.shopInfo = null;
      })
      .finally(() => {
        this.shopLoading = false;
        if (this.tab === 'shop' && this.root.visible) this.render();
      });
  }

  private price(stars: number): string {
    const usd = (stars * (this.shopInfo?.starsUsd ?? 0)).toFixed(2);
    return this.t('price_stars', { stars, usd });
  }

  private gearName(it: GearItem): string {
    return `${this.t(`rarity_${it.rarity}`)} ${this.theme.gear[it.slot].name[this.locale]}`;
  }

  /** Human lines for what a chest or pack gave, in the same voice as the end screen. */
  private resultLines(r: PurchaseResult): { text: string; color: number }[] {
    const out: { text: string; color: number }[] = [];
    if (r.copies) {
      const parts = (Object.keys(r.copies) as ArchetypeId[])
        .filter((a) => (r.copies?.[a] ?? 0) > 0)
        .sort((a, b) => (r.copies?.[b] ?? 0) - (r.copies?.[a] ?? 0))
        .map((a) => `${this.theme.units[a].name[this.locale]} ×${r.copies?.[a]}`);
      if (parts.length > 0) out.push({ text: parts.join(' · '), color: COLORS.gold });
    }
    if (r.gear) out.push({ text: `${this.gearName(r.gear)} · ${this.t(`gear_stat_${r.gear.slot}`, { n: r.gear.bonus })}`, color: RARITY_COLORS[r.gear.rarity] });
    if (r.gearLost) out.push({ text: this.t('inventory_full', { name: '' }).replace(/,\s*$/, ''), color: COLORS.danger });
    const xp = r.heroXp ?? r.xp ?? 0;
    if (xp > 0) out.push({ text: this.t('reward_hero_xp', { n: xp }), color: COLORS.gold });
    if (r.medals) out.push({ text: this.t('reward_medals', { n: r.medals }), color: COLORS.gold });
    const ups = (r.cardLevelUps ?? []).map((u) => this.t('level_up', { name: this.theme.units[u.arch].name[this.locale], n: u.level }));
    if (r.heroLevelUp) ups.push(this.t('hero_level_up', { n: r.heroLevelUp }));
    if (ups.length > 0) out.push({ text: ups.join(' · '), color: COLORS.hp });
    if (r.pityHit) out.push({ text: this.t('pity_now').replace(':', ' ✓'), color: RARITY_COLORS.epic });
    return out;
  }

  private productName(product: ProductId, arch?: ArchetypeId): string {
    if (product === 'copies') return this.t('copies_pack', { n: this.balance.shop.copiesPack.copies, name: arch ? this.theme.units[arch].name[this.locale] : '' });
    if (product === 'hero_xp') return this.t('hero_xp_pack', { n: this.balance.shop.heroXpPack.xp });
    if (product === 'starter') return this.t('starter_title');
    if (product === 'skin') return this.t('skins_title');
    return this.theme.shop[product].name[this.locale];
  }

  private startPurchase(product: ProductId, arch?: ArchetypeId, skin?: SkinId): void {
    const info = this.shopInfo;
    if (!info) return;
    const isChest = (CHEST_IDS as readonly string[]).includes(product);
    if (isChest && !info.adult) {
      this.showModal(this.t('adult_title'), [{ text: this.t('adult_text'), color: COLORS.text }], [
        { label: this.t('cancel'), color: 0x3a3f4b, onTap: () => this.closeModal() },
        {
          label: this.t('adult_ok'),
          color: COLORS.button,
          onTap: () => {
            this.closeModal();
            this.hooks.shop
              .consent()
              .then(() => {
                if (this.shopInfo) this.shopInfo.adult = true;
                this.startPurchase(product, arch);
              })
              .catch(() => this.showError('shop_error'));
          },
        },
      ]);
      return;
    }
    if (info.payments === 'off') {
      this.showError('payments_off');
      return;
    }
    this.showModal(this.productName(product, arch), [{ text: this.t('paying'), color: COLORS.textDim }], []);
    this.hooks.shop
      .buy(product, arch, skin)
      .then((result) => {
        if (!result) {
          this.showModal(this.productName(product, arch), [{ text: this.t('payment_cancelled'), color: COLORS.textDim }], [
            { label: this.t('close'), color: COLORS.button, onTap: () => this.closeModal() },
          ]);
          return;
        }
        this.showResult(isChest ? this.t('opened', { name: this.productName(product, arch) }) : this.t('purchase_done'), result);
      })
      .catch((err: unknown) => {
        const code = err instanceof Error ? err.message : '';
        this.showError(code === 'payment_failed' || code === 'payments_off' ? code : 'shop_error');
      })
      .finally(() => this.loadShop());
  }

  /** Leaderboard for one map: today, this week or all time, best run per player. */
  private showBoard(mapId: string, period: BoardPeriod = 'day'): void {
    const title = this.t('board_title', { name: this.t(`${mapId}_name`) });
    const periods: BoardPeriod[] = ['day', 'week', 'all'];
    const buttons = periods.map((p) => ({
      label: this.t(`board_${p}`),
      color: p === period ? COLORS.button : 0x3a3f4b,
      onTap: () => {
        if (p !== period) this.showBoard(mapId, p);
      },
      fontSize: 11,
    }));
    buttons.push({ label: this.t('close'), color: 0x6b3f3f, onTap: () => this.closeModal(), fontSize: 11 });
    this.showModal(title, [{ text: this.t('loading'), color: COLORS.textDim }], buttons);
    this.hooks
      .board(mapId, period)
      .then((d) => {
        const lines: { text: string; color: number }[] = [];
        if (d.leaderboard.length === 0) lines.push({ text: this.t('board_empty'), color: COLORS.textDim });
        d.leaderboard.slice(0, 10).forEach((e, i) => {
          lines.push({
            text: `${i + 1}. ${e.name}${e.me ? ` (${this.t('you')})` : ''} · ${e.score} · ${this.t('waves_short', { n: e.waves })}`,
            color: e.me ? COLORS.gold : COLORS.text,
          });
        });
        lines.push({ text: this.t('players_period', { n: d.players }), color: COLORS.textDim });
        if (d.me && d.me.best !== null && d.me.rank !== null) lines.push({ text: this.t('your_best_rank', { n: d.me.best, rank: d.me.rank }), color: COLORS.accent });
        this.showModal(title, lines, buttons);
      })
      .catch(() => this.showError('server_down'));
  }

  // ---------- skills ----------

  /** Skill tree: four branches, nodes in order; each row shows level, effect and the medal price. */
  private renderSkills(): void {
    const profile = this.hooks.profile();
    const levels = profile.skills;
    const medals = profile.medals;
    this.hint('skills_hint');
    const hintNode = this.content.children[this.content.children.length - 1] as Text;
    hintNode.text = this.t('skills_hint', { total: skillTreeTotal(this.balance) });
    const balanceTx = label(this.t('medals_have', { n: medals }), 20, COLORS.gold, 'bold');
    balanceTx.anchor.set(1, 0);
    balanceTx.x = L.W - 14;
    balanceTx.y = HEADER_H + 52;
    this.content.addChild(balanceTx);
    const act = (p: Promise<void>): void => {
      p.then(() => this.render()).catch((err: unknown) => {
        const code = err instanceof Error && 'code' in err ? String((err as { code: string }).code) : 'server_down';
        this.showError(code);
      });
    };
    let y = HEADER_H + 84;
    const branches: SkillBranch[] = ['fire', 'defense', 'economy', 'hero'];
    for (const br of branches) {
      const nodes = this.balance.skills.nodes.filter((n) => n.branch === br);
      if (nodes.length === 0) continue;
      const head = label(this.theme.skills.branches[br][this.locale], 14, COLORS.text, 'bold');
      head.x = 16;
      head.y = y;
      this.content.addChild(head);
      y += 24;
      for (const n of nodes) {
        const th = this.theme.skills.nodes[n.id];
        const lv = levels[n.id] ?? 0;
        const why = skillBlocker(levels, medals, n.id, this.balance);
        const cost = skillCost(n.id, lv, this.balance);
        const row = this.card(y, 70);
        const name = label(th.name[this.locale], 13, lv > 0 ? COLORS.text : COLORS.textDim, 'bold');
        name.x = 22;
        name.y = 8;
        const lvl = label(this.t('skill_level', { n: lv, max: n.maxLevel }), 10, lv >= n.maxLevel ? COLORS.hp : COLORS.accent, 'bold');
        lvl.x = 22 + Math.ceil(name.width) + 8;
        lvl.y = 11;
        const desc = label(th.desc[this.locale].replace('{n}', n.id === 'turret_range' ? (SKILL_STEP.turret_range / 1000).toFixed(2).replace(/0$/, '') : String(SKILL_STEP[n.id])), 10, COLORS.textDim);
        desc.x = 22;
        desc.y = 28;
        desc.style.wordWrap = true;
        desc.style.wordWrapWidth = L.W - 172;
        row.addChild(name, lvl, desc);
        // Level pips under the text
        const pips = new Graphics();
        for (let i = 0; i < n.maxLevel; i++) {
          pips.roundRect(22 + i * 14, 58, 10, 4, 2).fill(i < lv ? COLORS.gold : COLORS.hpBg);
        }
        row.addChild(pips);
        const bw = 118;
        if (why === 'max_level') {
          const done = label(this.t('skill_max'), 11, COLORS.hp, 'bold');
          done.anchor.set(1, 0.5);
          done.x = L.W - 22;
          done.y = 35;
          row.addChild(done);
        } else if (why === 'skill_locked' && n.requires) {
          const lock = label(this.t('skill_locked_by', { name: this.theme.skills.nodes[n.requires].name[this.locale] }), 9.5, COLORS.textDim);
          lock.anchor.set(1, 0.5);
          lock.x = L.W - 22;
          lock.y = 35;
          lock.style.wordWrap = true;
          lock.style.wordWrapWidth = 120;
          lock.style.align = 'right';
          row.addChild(lock);
        } else {
          const can = why === null;
          const btn = button(this.t('skill_upgrade', { n: cost ?? 0 }), can ? COLORS.button : COLORS.buttonDisabled, () => {
            if (can) act(this.hooks.skills.upgrade(n.id));
          }, bw, 34, 11);
          btn.x = L.W - 22 - bw;
          btn.y = 18;
          row.addChild(btn);
        }
        this.content.addChild(row);
        y += 76;
      }
      y += 8;
    }
  }

  private showResult(title: string, result: PurchaseResult): void {
    this.showModal(title, this.resultLines(result), [{ label: this.t('close'), color: COLORS.button, onTap: () => this.closeModal() }]);
  }

  private showError(key: string): void {
    this.showModal(this.t(key), [], [{ label: this.t('close'), color: COLORS.button, onTap: () => this.closeModal() }]);
  }

  private renderShop(): void {
    const info = this.shopInfo;
    this.hint('shop_intro');
    if (!info) {
      const tx = label(this.shopLoading ? this.t('loading') : this.t('server_down'), 13, COLORS.textDim);
      tx.anchor.set(0.5, 0);
      tx.x = L.W / 2;
      tx.y = 120;
      this.content.addChild(tx);
      if (!this.shopLoading) this.loadShop();
      return;
    }
    let y = 108;
    const free = this.theme.shop[info.freeChest];
    const act = (p: Promise<void>): void => {
      p.then(() => this.loadShop()).catch(() => this.showError('server_down'));
    };

    // Free daily chest: the reason to come back, no stake, so no gate.
    {
      const row = this.card(y, 64);
      const title = label(this.t('free_chest'), 14, COLORS.gold, 'bold');
      title.x = 22;
      title.y = 10;
      const sub = label(`${free.name[this.locale]} · ${free.desc[this.locale]}`, 10, COLORS.textDim);
      sub.x = 22;
      sub.y = 32;
      sub.style.wordWrap = true;
      sub.style.wordWrapWidth = L.W - 160;
      row.addChild(title, sub);
      const btn = button(
        info.freeChestAvailable ? this.t('open_free') : this.t('free_taken'),
        info.freeChestAvailable ? 0x3a7d44 : COLORS.buttonDisabled,
        () => {
          if (!info.freeChestAvailable) return;
          this.showModal(this.t('free_chest'), [{ text: this.t('loading'), color: COLORS.textDim }], []);
          this.hooks.shop
            .freeChest()
            .then((r) => this.showResult(this.t('opened', { name: free.name[this.locale] }), r))
            .catch(() => this.showError('shop_error'))
            .finally(() => this.loadShop());
        },
        104,
        34,
        12,
      );
      btn.x = L.W - 22 - 104;
      btn.y = 15;
      row.addChild(btn);
      this.content.addChild(row);
      y += 72;
    }

    // Chests: odds on the card, pity counter, region and age gates.
    // Starter bundle: once per account, discounted, fixed contents, no gate.
    if (info.starterAvailable) {
      const sp = info.starterPack;
      const row = this.card(y, 96);
      row.addChildAt(new Graphics().roundRect(10, 0, L.W - 20, 96, 10).stroke({ width: 2, color: COLORS.gold }), 1);
      const title = label(this.t('starter_title'), 14, COLORS.gold, 'bold');
      title.x = 22;
      title.y = 10;
      const badge = label(this.t('starter_badge', { pct: Math.round(100 - (100 * sp.stars) / sp.fullStars) }), 10, COLORS.text, 'bold');
      badge.x = 22 + Math.ceil(title.width) + 10;
      badge.y = 13;
      const badgeBg = new Graphics().roundRect(badge.x - 6, badge.y - 3, Math.ceil(badge.width) + 12, 18, 6).fill(0xb8651b);
      const contents = label(
        this.t('starter_contents', { n: sp.copiesEach, xp: sp.heroXp, medals: sp.medals, item: `${this.t(`rarity_${sp.gear.rarity}`)} ${this.theme.gear[sp.gear.slot].name[this.locale]} +${sp.gear.bonus}%` }),
        10,
        COLORS.textDim,
      );
      contents.x = 22;
      contents.y = 34;
      contents.style.wordWrap = true;
      contents.style.wordWrapWidth = L.W - 190;
      const was = label(this.t('starter_was', { n: sp.fullStars }), 9.5, COLORS.textDim);
      was.anchor.set(1, 0);
      was.x = L.W - 22;
      was.y = 70;
      row.addChild(badgeBg, title, badge, contents, was);
      const btn = button(this.price(sp.stars), info.payments === 'off' ? COLORS.buttonDisabled : 0xb8651b, () => this.startPurchase('starter'), 128, 34, 12);
      btn.x = L.W - 22 - 128;
      btn.y = 30;
      row.addChild(btn);
      this.content.addChild(row);
      y += 104;
    }

    const chestsHead = label(this.t('chests_title'), 14, COLORS.text, 'bold');
    chestsHead.x = 16;
    chestsHead.y = y;
    this.content.addChild(chestsHead);
    const pity = label(info.pityLeft === 0 ? this.t('pity_now') : this.t('pity_left', { n: info.pityLeft }), 10, RARITY_COLORS.epic);
    pity.anchor.set(1, 0);
    pity.x = L.W - 16;
    pity.y = y + 3;
    this.content.addChild(pity);
    y += 24;
    if (!info.region.chestsAllowed) {
      const blocked = label(this.t('region_blocked'), 11, COLORS.danger);
      blocked.style.wordWrap = true;
      blocked.style.wordWrapWidth = L.W - 32;
      blocked.x = 16;
      blocked.y = y;
      this.content.addChild(blocked);
      y += Math.ceil(blocked.height) + 8;
    }
    for (const chest of info.chests) {
      const th = this.theme.shop[chest.id];
      const row = this.card(y, 122);
      const chestPic = art.sprite(`chest:${chest.id}`, 52, 52);
      if (chestPic) {
        chestPic.x = L.W - 22 - 26;
        chestPic.y = 34;
        row.addChild(chestPic);
      }
      const name = label(th.name[this.locale], 14, COLORS.text, 'bold');
      name.x = 22;
      name.y = 8;
      const desc = label(th.desc[this.locale], 10, COLORS.textDim);
      desc.x = 22;
      desc.y = 27;
      desc.style.wordWrap = true;
      desc.style.wordWrapWidth = chestPic ? L.W - 124 : L.W - 60;
      const contents = label(
        chest.heroXp > 0
          ? this.t('chest_contents', { copies: chest.copies, word: this.copiesWord(chest.copies), xp: chest.heroXp, m: chest.medals })
          : this.t('chest_contents_noxp', { copies: chest.copies, word: this.copiesWord(chest.copies), m: chest.medals }),
        10,
        COLORS.gold,
      );
      contents.x = 22;
      contents.y = 56;
      const odds = label(this.t('chest_odds', { c: chest.gearOdds.common, r: chest.gearOdds.rare, e: chest.gearOdds.epic }), 9.5, COLORS.accent);
      odds.x = 22;
      odds.y = 70;
      row.addChild(name, desc, contents, odds);
      const enabled = info.region.chestsAllowed && info.payments !== 'off';
      const btn = button(this.price(chest.stars), enabled ? COLORS.button : COLORS.buttonDisabled, () => enabled && this.startPurchase(chest.id), 128, 30, 11.5);
      btn.x = L.W - 22 - 128;
      btn.y = 86;
      row.addChild(btn);
      this.content.addChild(row);
      y += 130;
    }

    // Field skins: cosmetics, one purchase each, the default free; the worn one is marked.
    {
      y += 6;
      const skinsHead = label(this.t('skins_title'), 14, COLORS.text, 'bold');
      skinsHead.x = 16;
      skinsHead.y = y;
      this.content.addChild(skinsHead);
      const skinsHint = label(this.t('skins_hint'), 9.5, COLORS.textDim);
      skinsHint.anchor.set(1, 0);
      skinsHint.x = L.W - 16;
      skinsHint.y = y + 3;
      this.content.addChild(skinsHint);
      y += 24;
      const tileW = Math.floor((L.W - 32 - 3 * 8) / 4);
      info.skins.forEach((sk, i) => {
        const owned = info.skinsOwned.includes(sk.id);
        const worn = info.skin === sk.id;
        const pal = FIELD_PALETTES[sk.id] ?? FIELD_PALETTES['default']!;
        const c = new Container();
        c.x = 16 + i * (tileW + 8);
        c.y = y;
        c.addChild(new Graphics().roundRect(0, 0, tileW, 108, 10).fill(0x232833).stroke({ width: worn ? 2 : 1, color: worn ? COLORS.gold : COLORS.panelLine }));
        // Swatch: water, shore, grass and a dirt stripe.
        const sw = new Graphics();
        sw.roundRect(6, 6, tileW - 12, 46, 6).fill(pal.water);
        sw.roundRect(10, 12, tileW - 20, 36, 5).fill(pal.shore);
        sw.roundRect(13, 15, tileW - 26, 30, 4).fill(pal.grassNear);
        sw.rect(13, 22, tileW - 26, 8).fill(pal.grassFar);
        sw.roundRect(tileW / 2 - 6, 15, 12, 30, 2).fill(pal.dirt);
        c.addChild(sw);
        const nm = label(this.t(`skin_${sk.id}`), 10, COLORS.text, 'bold');
        nm.anchor.set(0.5, 0);
        nm.x = tileW / 2;
        nm.y = 56;
        c.addChild(nm);
        const btnLabel = worn ? this.t('skin_worn') : owned ? this.t('skin_wear') : sk.stars > 0 ? `${sk.stars} ⭐` : this.t('skin_wear');
        const btn = button(btnLabel, worn ? 0x3a7d44 : owned ? COLORS.button : info.payments === 'off' ? COLORS.buttonDisabled : 0xb8651b, () => {
          if (worn) return;
          if (owned) act(this.hooks.shop.selectSkin(sk.id));
          else this.startPurchase('skin', undefined, sk.id);
        }, tileW - 12, 26, 10);
        btn.x = 6;
        btn.y = 74;
        c.addChild(btn);
        this.content.addChild(c);
      });
      y += 118;
    }

    // Fixed packs: no randomness, no gate, available everywhere.
    y += 6;
    const directHead = label(this.t('direct_title'), 14, COLORS.text, 'bold');
    directHead.x = 16;
    directHead.y = y;
    this.content.addChild(directHead);
    y += 24;
    {
      const row = this.card(y, 44);
      const name = label(this.t('hero_xp_pack', { n: info.heroXpPack.xp }), 12, COLORS.text, 'bold');
      name.x = 22;
      name.y = 13;
      row.addChild(name);
      const btn = button(this.price(info.heroXpPack.stars), info.payments === 'off' ? COLORS.buttonDisabled : COLORS.button, () => this.startPurchase('hero_xp'), 128, 30, 11.5);
      btn.x = L.W - 22 - 128;
      btn.y = 7;
      row.addChild(btn);
      this.content.addChild(row);
      y += 50;
    }
    const profile = this.hooks.profile();
    ARCHETYPES.forEach((arch) => {
      const th = this.theme.units[arch];
      const row = this.card(y, 44);
      const pic = art.sprite(`unit:${arch}`, 36, 36);
      if (pic) {
        pic.x = 30;
        pic.y = 22;
        row.addChild(pic);
      } else {
        const icon = new Graphics().roundRect(16, 8, 28, 28, 7).fill(hexToNum(th.color)).stroke({ width: 1.5, color: COLORS.ink });
        drawGlyph(icon, arch, 30, 22, 5.5);
        row.addChild(icon);
      }
      const name = label(this.t('copies_pack', { n: info.copiesPack.copies, name: th.name[this.locale] }), 10.5, COLORS.text, 'bold');
      name.x = 54;
      name.y = 7;
      const card = profile.cards[arch] ?? { level: 1, copies: 0 };
      const need = cardCopiesToNext(card.level, this.balance);
      const sub = label(
        need === null ? this.t('card_max') : `${this.t('card_level', { n: card.level })} · ${this.t('copies_to_next', { n: card.copies, total: need, lvl: card.level + 1 })}`,
        9,
        COLORS.textDim,
      );
      sub.x = 54;
      sub.y = 24;
      row.addChild(name, sub);
      const btn = button(this.price(info.copiesPack.stars), info.payments === 'off' ? COLORS.buttonDisabled : COLORS.button, () => this.startPurchase('copies', arch), 108, 30, 10.5);
      btn.x = L.W - 22 - 108;
      btn.y = 7;
      row.addChild(btn);
      this.content.addChild(row);
      y += 50;
    });
  }
}
